import { useTranslation } from 'react-i18next';

import { type UseMutationResult, useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { ToastData } from '~/hooks/useToast';
import toast from '~/utils/toast';

export interface ActionResponse {
  toast?: ToastData;
}

export class ActionError extends Error {
  toast?: ToastData;

  constructor(toast?: ToastData) {
    super(toast?.message ?? 'Community action failed');
    this.name = 'ActionError';
    this.toast = toast;
  }
}

/**
 * POSTs to a community `__actions/*` route. Returns the parsed response, such as `{ toast }`, or throws an
 * `ActionError` carrying the server's toast on a non-2xx response.
 */
export const submitCommunityAction = async <Data extends ActionResponse = ActionResponse>(
  path: string,
  fields: Record<string, string>,
  csrfToken = '',
): Promise<Data> => {
  const response = await fetch(path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: new URLSearchParams(fields),
  });

  const data = (await response.json().catch(() => null)) as Data | null;

  if (!response.ok) {
    throw new ActionError(data?.toast);
  }

  return data ?? ({} as Data);
};

export interface CommunityActionBaseConfig<Vars, TContext, Data extends ActionResponse = ActionResponse> {
  buildRequest: (vars: Vars) => { path: string; fields: Record<string, string> };
  onMutate?: (vars: Vars) => TContext;
  onError?: (error: unknown, vars: Vars, context: TContext | undefined) => void;
  onSuccess?: (data: Data, vars: Vars, context: TContext | undefined) => void;
  onSettled?: (data?: Data, error?: unknown, vars?: Vars) => void;
  showSuccessToast?: boolean;
}

/**
 * Cache-agnostic base for community `__actions/*` mutations: POSTs the action and toasts
 * the server's response (or a translated generic error). Owns no cache logic — callers
 * wire optimistic updates and invalidation through the optional lifecycle hooks. For
 * feed/comment actions, prefer `useCommunityFeedActionMutation`.
 */
export const useCommunityActionBase = <Vars, TContext = unknown, Data extends ActionResponse = ActionResponse>({
  buildRequest,
  onMutate,
  onError,
  onSuccess,
  onSettled,
  showSuccessToast = true,
}: CommunityActionBaseConfig<Vars, TContext, Data>): UseMutationResult<Data, unknown, Vars, TContext> => {
  const { t } = useTranslation();
  const ensureCsrfToken = useEnsureCsrfToken();

  return useMutation<Data, unknown, Vars, TContext>({
    mutationFn: async (vars) => {
      const { path, fields } = buildRequest(vars);
      return submitCommunityAction<Data>(path, fields, await ensureCsrfToken());
    },
    onMutate,
    onError: (error, vars, context) => {
      onError?.(error, vars, context);
      const serverToast = error instanceof ActionError ? error.toast : undefined;
      toast.error(serverToast?.message ?? t('community.action_menu.generic_error'));
    },
    onSuccess: (data, vars, context) => {
      onSuccess?.(data, vars, context);
      if (showSuccessToast && data.toast) toast.success(data.toast.message);
    },
    onSettled,
  });
};
